import { InfoboxProps } from "design-system/components/blocks/infobox/infobox";
import {
  CarouselProps,
  TeaseCarousel,
} from "design-system/components/blocks/tease-carousel/tease-carousel";
import {
  CarouselCardProps,
  TeaseCarouselCard,
} from "design-system/components/blocks/tease-carousel/tease-carousel-card";
import { TeaseCarouselItem } from "design-system/components/blocks/tease-carousel/tease-carousel-item";
import {
  TeaseRow,
  // TeaseRow,
  TeaseRowProps,
} from "design-system/components/blocks/tease-row/tease-row";
import { LazyTeaseRowTease } from "design-system/components/blocks/tease-row/tease-row-tease";
import { Truncator } from "design-system/components/blocks/truncator/truncator";
import { DetailPageBodyProps } from "design-system/components/pages/detail-page/detail-page-body/detail-page-body";
import { ArticleTeaseProps } from "design-system/components/primitives/article-tease/article-tease";
import { ComponentHeader } from "design-system/components/primitives/component-header/component-header";
import { MediaAssetImageProps } from "design-system/components/primitives/media-asset/media-asset-image";

import { ParticipantStoryTeaseProps } from "design-system/components/primitives/participant-story-tease/participant-story-tease";
import { Theme } from "design-system/utils/theme";
import { DataLayerEvent } from "frontend/hooks/use-data-layer";
import {
  KnowledgeAlumniFields,
  KnowledgeAlumniInfoFields,
  KnowledgeFacultyAuthors,
  KnowledgeFacultyDetails,
  KnowledgeFacultyInfoFields,
  KnowledgeRelatedAlumni,
  KnowledgeRelatedFaculty,
  KnowledgeRestructuredInfobox,
} from "frontend/types";

function getInfoBoxTitle(infobox: KnowledgeRestructuredInfobox) {
  if (infobox.alumInfobox?.displayName) {
    return infobox.alumInfobox?.displayName;
  }
  if (infobox.caseInfobox?.caseTitle) {
    return infobox.caseInfobox.caseTitle;
  }
  if (infobox.companyInfobox?.displayName) {
    return infobox.companyInfobox.displayName;
  } else if (infobox.companyInfobox?.companyTitle) {
    return infobox.companyInfobox.companyTitle;
  }
  if (infobox.courseInfobox?.courseName) {
    return infobox.courseInfobox.courseName;
  }
  if (infobox.facultyInfobox?.displayName) {
    return infobox.facultyInfobox.displayName;
  }
  if (infobox.initiativeInfobox?.initiativeTitle) {
    return infobox.initiativeInfobox.initiativeTitle;
  }
  if (infobox.topicInfobox?.title) {
    return infobox.topicInfobox.title;
  }
  return "";
}

function getInfoBoxType(infobox: KnowledgeRestructuredInfobox) {
  if (infobox.alumInfobox) {
    return "Profile";
  }
  if (infobox.caseInfobox) {
    return "Case";
  }
  if (infobox.companyInfobox) {
    return "Company";
  }
  if (infobox.courseInfobox) {
    return "Course";
  }
  if (infobox.facultyInfobox) {
    return "Profile";
  }
  if (infobox.initiativeInfobox) {
    return "Initiative";
  }
  if (infobox.topicInfobox) {
    return "Topic";
  }
  return "";
}

function getFacultyImage(
  feature?:
    | KnowledgeRelatedFaculty
    | KnowledgeFacultyDetails
    | KnowledgeFacultyInfoFields,
): MediaAssetImageProps {
  const image: MediaAssetImageProps = { src: "" };
  let facultyId = undefined;
  if (typeof feature === "object") {
    if ("displayName" in feature) {
      facultyId = feature.facultyId;
      image.alt = feature.displayName;
    } else if ("profilePage" in feature) {
      facultyId = feature.profilePage;
      image.alt = feature.name;
    } else if ("facultyName" in feature) {
      facultyId = feature.facultyId;
      image.alt = feature.facultyName;
    }
  }
  if (facultyId?.includes("?facId=")) {
    const id = facultyId?.split("?facId=")[1];
    if (id) facultyId = id;
  }
  if (facultyId && /^\d+$/.test(facultyId)) {
    // Assume image exists if facultyId is a #
    image.src = `/api/headshot/${facultyId}`;
  }
  // No Photo
  if (!facultyId || !image.src) {
    image.src = "/api/headshot/0";
    image.alt = "default hbs tower";
  }
  return image;
}

function getAlumniImage(
  feature?:
    | KnowledgeAlumniFields
    | KnowledgeAlumniInfoFields
    | KnowledgeRelatedAlumni,
): MediaAssetImageProps {
  const image: MediaAssetImageProps = { src: "" };
  let alumniId = undefined;
  if (typeof feature === "object") {
    if ("alumId" in feature) {
      alumniId = feature.alumId;
      image.alt = feature.displayName;
    } else if ("alumniProfileUrl" in feature) {
      alumniId = feature.alumniProfileUrl;
      image.alt = feature.name;
    } else if ("profileUrl" in feature) {
      alumniId = feature.profileUrl;
      image.alt = feature.name;
    }
  }
  if (alumniId?.includes("?alumId=")) {
    const id = alumniId?.split("?alumId=")[1];
    if (id) alumniId = id;
  }
  if (image.alt && image.alt?.indexOf(", ") > -1) {
    // Remove appended title from name e.g. ", MBA 2017"
    image.alt = image.alt.split(", ")[0];
  }
  if (alumniId && /^\d+$/.test(alumniId)) {
    // Assume image exists if alumniId is a #
    image.src = `/api/internal/alumni/photo/${alumniId}`;
  }
  // No Photo
  if (!alumniId || !image.src) {
    image.src = "/api/headshot/0";
    image.alt = "default hbs tower";
  }
  return image;
}

export function getInfoBox(
  infobox: KnowledgeRestructuredInfobox,
  detailPage?: boolean,
): InfoboxProps | undefined {
  const props: InfoboxProps = {
    type: "",
    name: "",
    image: { src: "" },
    link: { children: undefined, href: undefined },
  };
  if (!infobox) return undefined;
  const seeAll = "See All Connections";
  if (infobox.alumInfobox) {
    props.type = getInfoBoxType(infobox);
    if (infobox.alumInfobox.displayName) {
      props.name = getInfoBoxTitle(infobox);
    }
    if (infobox.alumInfobox.alumId && props.image) {
      const image = getAlumniImage(infobox.alumInfobox);
      props.image.src = image.src;
      props.image.alt = image.alt;
    }
    if (detailPage) {
      const meta = [];
      if (infobox.alumInfobox.currentCompany) {
        const { name, companyType, headquarters, industry } =
          infobox.alumInfobox.currentCompany;
        if (name) {
          meta.push({
            label: "Current Company",
            description: name,
          });
        }
        if (companyType) {
          meta.push({
            label: "Company Type",
            description: companyType,
          });
        }
        if (headquarters) {
          meta.push({
            label: "Company Headquarters",
            description: headquarters,
          });
        }
        if (industry) {
          meta.push({
            label: "Company Industry",
            description: industry,
          });
        }
      }
      if (infobox.alumInfobox.profileUrl) {
        props.link = props.link || {};
        props.link.href = infobox.alumInfobox.profileUrl;
        props.link.children = props.link.href ? "View Profile" : undefined;
      }
      props.metadata = meta;
    }
    if (infobox.alumInfobox.conceptUri && !detailPage) {
      props.url = infobox.alumInfobox.conceptUri;
      props.link = props.link || {};
      props.link.href = infobox.alumInfobox.conceptUri;
      props.link.children = props.link.href ? seeAll : undefined;
    }
  }

  if (infobox.caseInfobox) {
    props.type = getInfoBoxType(infobox);
    if (infobox.caseInfobox.conceptUri && !detailPage) {
      props.url = infobox.caseInfobox.conceptUri;
      props.link = props.link || {};
      props.link.href = infobox.caseInfobox.conceptUri;
      props.link.children = props.link.href ? seeAll : undefined;
    }
    if (infobox.caseInfobox.caseTitle) {
      props.name = getInfoBoxTitle(infobox);
    }
    if (infobox.caseInfobox.caseNumber && detailPage) {
      const meta = [];
      meta.push({
        label: "Case Number",
        description: infobox.caseInfobox.caseNumber,
      });
      if (infobox.caseInfobox.caseAuthors) {
        const authors: Array<string> = [];
        const nonHBSAuthors: Array<string> = [];
        infobox.caseInfobox.caseAuthors.forEach((author) => {
          if (author.isHBSFaculty === "HBSFaculty") {
            if (author.authorName) authors.push(author.authorName);
          } else {
            if (author.authorName) nonHBSAuthors.push(author.authorName);
          }
        });
        meta.push({
          label: "By",
          description: [authors.join(", "), nonHBSAuthors.join(", ")]
            .filter(Boolean)
            .join(", "),
        });
      }
      meta.push({
        label: "Publication Date",
        description: infobox.caseInfobox.caseDisplayDate,
      });
      props.metadata = meta;
    }

    if (infobox.caseInfobox.caseDescription) {
      props.description = <p>{infobox.caseInfobox.caseDescription}</p>;
    }
  }

  if (infobox.companyInfobox) {
    props.type = getInfoBoxType(infobox);
    if (
      infobox.companyInfobox.displayName ||
      infobox.companyInfobox.companyTitle
    ) {
      props.name = getInfoBoxTitle(infobox);
    }
    if (infobox.companyInfobox.companyDescription) {
      props.description = infobox.companyInfobox.companyDescription;
    }
    if (infobox.companyInfobox.conceptUri && !detailPage) {
      props.url = infobox.companyInfobox.conceptUri;
      props.link = props.link || {};
      props.link.href = infobox.companyInfobox.conceptUri;
      props.link.children = props.link.href ? seeAll : undefined;
    }
    if (detailPage) {
      if (infobox.companyInfobox.capIqDetails) {
        const metadata = [];
        const {
          headquarters,
          yearFounded,
          ceo,
          cfo,
          companyStatus,
          industrySector,
          industryGroup,
          ticker,
        } = infobox.companyInfobox.capIqDetails;

        if (headquarters) {
          metadata.push({ label: "Headquarters", description: headquarters });
        }
        if (ceo) metadata.push({ label: "CEO", description: ceo });
        if (companyStatus) {
          metadata.push({
            label: "Company Status",
            description: companyStatus,
          });
        }
        if (infobox.companyInfobox.companyPrecursor) {
          metadata.push({
            label: "Past Company Name(s)",
            description: infobox.companyInfobox.companyPrecursor
              .map((company) => company)
              .join(", "),
          });
        }
        if (cfo) metadata.push({ label: "CFO", description: cfo });

        if (ticker) {
          metadata.push({
            label: "Ticker",
            description: ticker,
          });
        }
        if (yearFounded) {
          metadata.push({
            label: "Founding Date",
            description: yearFounded,
          });
        }
        if (industrySector) {
          metadata.push({
            label: "Industry Sector",
            description: industrySector,
          });
        }
        if (industrySector) {
          metadata.push({
            label: "Industry",
            description: industryGroup,
          });
        }
        if (infobox.companyInfobox.companySuccessor) {
          metadata.push({
            label: "Company Successor Name(s)",
            description: infobox.companyInfobox.companySuccessor
              .map((company) => company)
              .join(", "),
          });
        }
        props.metadata = metadata;
        if (infobox.companyInfobox.companyPrimaryUrl) {
          props.link = props.link || {};
          props.link.href =
            "https://" + infobox.companyInfobox.companyPrimaryUrl;
          props.link.children = props.link.href ? "Website" : undefined;
        }
      }
    }
  }

  if (infobox.courseInfobox) {
    props.type = getInfoBoxType(infobox);
    if (infobox.courseInfobox.conceptUri && !detailPage) {
      props.url = infobox.courseInfobox.conceptUri;
      props.link = props.link || {};
      props.link.href = infobox.courseInfobox.conceptUri;
      props.link.children = props.link.href ? seeAll : undefined;
    }
    if (infobox.courseInfobox.courseName) {
      props.name = getInfoBoxTitle(infobox);
    }
    if (infobox.courseInfobox.courseDescription) {
      props.description = infobox.courseInfobox.courseDescription;
    }
    if (infobox.courseInfobox.courseNumber && detailPage) {
      props.metadata = [];
      props.metadata.push({
        label: "Course Number",
        description: infobox.courseInfobox.courseNumber,
      });
      if (infobox.courseInfobox.courseCurriculum) {
        props.metadata.push({
          label: "Part of Curriculum",
          description: infobox.courseInfobox.courseCurriculum,
        });
      }
    }
  }

  if (infobox.facultyInfobox) {
    props.type = getInfoBoxType(infobox);
    props.metadata = [];
    if (infobox.facultyInfobox.displayName) {
      props.name = getInfoBoxTitle(infobox);
    }
    if (infobox.facultyInfobox.description) {
      props.descriptionLabel = "About";
      props.description = infobox.facultyInfobox?.description;
    }
    if (infobox.facultyInfobox.conceptUri && !detailPage) {
      props.url = infobox.facultyInfobox.conceptUri;
      props.link = props.link || {};
      props.link.href = infobox.facultyInfobox.conceptUri;
      props.link.children = props.link.href ? seeAll : undefined;
    }
    if (infobox.facultyInfobox.facultyId && props.image) {
      const image = getFacultyImage(infobox.facultyInfobox);
      props.image.src = image.src;
      props.image.alt = image.alt;
    }

    if (infobox.facultyInfobox.position?.length) {
      const positions = infobox.facultyInfobox.position
        .filter(Boolean)
        .join(", ");
      props.intro = positions;
    }

    if (detailPage) {
      if (infobox.facultyInfobox.facultyTitles?.length) {
        const titles = infobox.facultyInfobox.facultyTitles
          .map((titles) =>
            [
              titles.title,
              [
                titles.startYear,
                titles.endYear ? titles.endYear : "present",
              ].join("-"),
            ].join(", "),
          )
          .join("; ");
        props.metadata.push({
          label: "Faculty Title(s)",
          description: titles,
        });
      }
      if (infobox.facultyInfobox.units?.length) {
        const positions = infobox.facultyInfobox.units
          .map((unit) =>
            [
              unit.unit,
              [unit.startYear, unit.endYear ? unit.endYear : "present"].join(
                "-",
              ),
            ].join(", "),
          )
          .join("; ");
        props.metadata.push({
          label: "HBS Academic Unit(s)",
          description: positions,
        });
      }
      if (infobox.facultyInfobox.fields?.length) {
        const fields = infobox.facultyInfobox.fields
          .map((field) =>
            [
              field.field,
              [field.startYear, field.endYear ? field.endYear : "present"].join(
                "-",
              ),
            ].join(", "),
          )
          .join("; ");
        props.metadata.push({
          label: "HBS Academic Field(s)",
          description: fields,
        });
      }
      if (infobox.facultyInfobox.alumni) {
        props.metadata.push({
          label: "Alumni",
          description: infobox.facultyInfobox.alumni.degreeAsAlumni,
        });
      }
      if (infobox.facultyInfobox.primaryUrl) {
        props.link = props.link || {};
        props.link.href = infobox.facultyInfobox.primaryUrl;
        props.link.children = props.link.href
          ? infobox.facultyInfobox.alumni?.alumniProfileUrl
            ? "Faculty Profile"
            : "View Profile"
          : undefined;
      }

      if (infobox.facultyInfobox.alumni?.alumniProfileUrl) {
        props.secondaryLink = {};
        props.secondaryLink.href =
          infobox.facultyInfobox.alumni?.alumniProfileUrl;
        props.secondaryLink.children = props.secondaryLink.href
          ? "Alumni Profile"
          : undefined;
      }
    }
  }

  if (infobox.initiativeInfobox) {
    props.type = getInfoBoxType(infobox);
    if (infobox.initiativeInfobox.initiativeTitle) {
      props.name = getInfoBoxTitle(infobox);
    }
    if (infobox.initiativeInfobox.conceptUri && !detailPage) {
      props.url = infobox.initiativeInfobox.conceptUri;
      props.link = props.link || {};
      props.link.href = infobox.initiativeInfobox.conceptUri;
      props.link.children = props.link.href ? seeAll : undefined;
    }
    if (infobox.initiativeInfobox.intiativeDescription) {
      props.description = infobox.initiativeInfobox.intiativeDescription;
    }
  }

  if (infobox.topicInfobox) {
    props.type = getInfoBoxType(infobox);
    props.link = undefined;
    if (infobox.topicInfobox.title) {
      props.name = getInfoBoxTitle(infobox);
    }
    if (infobox.topicInfobox.definition) {
      props.description = infobox.topicInfobox.definition;
    }

    if (infobox.topicInfobox.conceptUri && !detailPage) {
      props.url = infobox.topicInfobox.conceptUri;
      props.link = props.link || {};
      props.link.href = infobox.topicInfobox.conceptUri;
      props.link.children = props.link.href ? seeAll : undefined;
    }
  }

  props.url = getKnowledgeDynamicUrl(props.url);
  if (props.link?.href) {
    props.link.href = getKnowledgeDynamicUrl(props.link.href);
  } else {
    props.link = undefined;
  }

  if (props.name) return props;
  return undefined;
}

export function getKnowledgeDynamicUrl(url?: string): string | undefined {
  if (!url) return undefined;

  // Return the url as ius if its alumni or faculty page url
  if (
    url.indexOf("alumni.hbs.edu/") > -1 ||
    url.indexOf("hbs.edu/faculty/") > -1
  ) {
    return url;
  }

  if (url.indexOf(".hbs.edu/") > -1) {
    const uri = url.split(".hbs.edu/")[1];

    // Disable these links for now since the API doesn't return any data
    if (url.indexOf("FandR") > -1) return undefined;
    if (url.indexOf("WkArticle") > -1) return undefined;
    if (url.indexOf("MBA/Topics") > -1) return undefined;

    return (
      "/knowledge/explore/" +
      (uri && uri.indexOf("/") > -1
        ? uri?.replaceAll("/", "--")
        : uri
          ? uri
          : "")
    );
  }
  if (url.startsWith("http")) {
    return url;
  } else {
    return "https://" + url;
  }
}

export function getTeaseRow(
  articles: Array<ArticleTeaseProps>,
  title: string,
): TeaseRowProps | undefined {
  const articleTeases: Array<LazyTeaseRowTease> = [];
  articles.map((feature) =>
    articleTeases.push({ name: "ArticleTease", props: feature }),
  );

  const featuringRow: TeaseRowProps = {
    headerContent: {
      isSmall: false,
      title: title,
    },
    align: "full",
    teases: articleTeases,
  };
  return featuringRow;
}

const dataLayerEvent: DataLayerEvent = {
  /* eslint-disable camelcase */
  event: "info_box_item_click",
  event_data: {},
  /* eslint-enable camelcase */
};

export function getCarousel(
  cards: Array<CarouselCardProps>,
  title: string,
  colorStart = 0,
  infoboxName: string,
  infoboxCategory: string,
): CarouselProps {
  const themes: Array<Theme> = [
    "crimson",
    "blue",
    "purple",
    "red",
    "dark",
    "blue",
  ];

  /* eslint-disable camelcase */
  dataLayerEvent.event_data.infobox_name = infoboxName;
  dataLayerEvent.event_data.infobox_category = infoboxCategory;
  const handleOnClick = () => {
    dataLayerEvent.event_data.infobox_item_type = title;
  };
  /* eslint-enable camelcase */

  const props: CarouselProps = {
    description: <ComponentHeader isSmall={false} title={title} />,
    align: "full",
    children:
      cards.length > 0
        ? cards.map((feature, i) => (
            <TeaseCarouselItem key={i}>
              <TeaseCarouselCard
                {...feature}
                theme={themes[(colorStart + i) % themes.length]}
                dataLayer={dataLayerEvent}
                onClick={handleOnClick}
              />
            </TeaseCarouselItem>
          ))
        : null,
  };
  return props;
}

export function getRelatedInfoBox(
  infobox: KnowledgeRestructuredInfobox,
): DetailPageBodyProps | undefined {
  const props: InfoboxProps = {
    type: "",
    name: "",
    image: { src: "" },
    link: { children: undefined, href: undefined },
  };

  let title = "";
  let total = 0;
  let teaseRow = 0;
  let featuring: Array<CarouselCardProps> = [];
  let featuring1: Array<CarouselCardProps> = [];
  let authorTease4Up: Array<LazyTeaseRowTease> = [];
  let authorTeaseTruncated: Array<LazyTeaseRowTease> = [];
  let handleOnClick: (() => void) | undefined;

  const pageBody: DetailPageBodyProps = { children: <></> };

  if (
    !infobox.alumInfobox &&
    !infobox.caseInfobox &&
    !infobox.companyInfobox &&
    !infobox.courseInfobox &&
    !infobox.facultyInfobox &&
    !infobox.initiativeInfobox &&
    !infobox.topicInfobox
  ) {
    pageBody.children = (
      <>
        {pageBody.children}
        <div className="related-placeholder"></div>
      </>
    );
    return pageBody;
  }

  if (infobox.alumInfobox) {
    teaseRow = 0;
    //Initiative, Featured in
    if (infobox.alumInfobox.initiativeAffiliationWithConceptUri) {
      title = "Initiative Affiliation";
      total = 0;
      featuring = [];

      infobox.alumInfobox.initiativeAffiliationWithConceptUri.forEach(
        (feature) => {
          if (feature.initiativeAffiliation && total < 25) {
            featuring.push({
              title: feature.initiativeAffiliation,
              link: getKnowledgeDynamicUrl(feature.uri),
            });
            total++;
          }
        },
      );

      const infoboxName = getInfoBoxTitle(infobox);
      const infoboxCategory = getInfoBoxType(infobox);
      const featuringProps = getCarousel(
        featuring,
        title,
        teaseRow,
        infoboxName,
        infoboxCategory,
      );
      teaseRow++;
      pageBody.children = (
        <>
          {pageBody.children}
          <TeaseCarousel {...featuringProps} />
        </>
      );
    }

    if (infobox.alumInfobox.featuringStories) {
      title = `${infobox.alumInfobox.displayName} Featured In`;
      total = 0;
      featuring = [];

      infobox.alumInfobox.featuringStories.forEach((feature) => {
        if (feature.title && total < 25) {
          const url =
            feature.contentType === "Case"
              ? getKnowledgeDynamicUrl(feature.uri)
              : feature.url;
          featuring.push({
            title: feature.title,
            link: url,
          });
          total++;
        }
      });

      const infoboxName = getInfoBoxTitle(infobox);
      const infoboxCategory = getInfoBoxType(infobox);
      const featuringProps = getCarousel(
        featuring,
        title,
        teaseRow,
        infoboxName,
        infoboxCategory,
      );
      teaseRow++;
      pageBody.children = (
        <>
          {pageBody.children}
          <TeaseCarousel {...featuringProps} />
        </>
      );
    }

    return pageBody;
  }

  if (infobox.caseInfobox) {
    // Case: Organization,Course,Initiative,Topic, Geo Area, Industry
    if (infobox.caseInfobox.caseOrganizationWithConceptUri?.length) {
      title = "About Organization";
      total = 0;
      featuring = [];

      infobox.caseInfobox.caseOrganizationWithConceptUri.forEach((feature) => {
        if (feature.CaseOrganization && total < 25) {
          featuring.push({
            title: feature.CaseOrganization,
            link: getKnowledgeDynamicUrl(feature.uri),
          });
          total++;
        }
      });
      const infoboxName = getInfoBoxTitle(infobox);
      const infoboxCategory = getInfoBoxType(infobox);
      const featuringProps = getCarousel(
        featuring,
        title,
        teaseRow,
        infoboxName,
        infoboxCategory,
      );
      teaseRow++;
      pageBody.children = (
        <>
          {pageBody.children}
          <TeaseCarousel {...featuringProps} />
        </>
      );
    }
    if (infobox.caseInfobox.caseAssignedInCourseWithConceptUri?.length) {
      title = "Assigned In Course";
      total = 0;
      featuring = [];

      infobox.caseInfobox.caseAssignedInCourseWithConceptUri.forEach(
        (feature) => {
          if (feature.CaseAssignedInCourse && total < 25) {
            featuring.push({
              title: feature.CaseAssignedInCourse,
              link: getKnowledgeDynamicUrl(feature.uri),
            });
            total++;
          }
        },
      );
      const infoboxName = getInfoBoxTitle(infobox);
      const infoboxCategory = getInfoBoxType(infobox);
      const featuringProps = getCarousel(
        featuring,
        title,
        teaseRow,
        infoboxName,
        infoboxCategory,
      );
      teaseRow++;
      pageBody.children = (
        <>
          {pageBody.children}
          <TeaseCarousel {...featuringProps} />
        </>
      );
    }

    if (infobox.caseInfobox.caseInitiativesWithConceptUri?.length) {
      const relatedIntiative = `Related Initiative`;
      total = 0;
      featuring = [];

      infobox.caseInfobox.caseInitiativesWithConceptUri.forEach((feature) => {
        if (feature.CaseInitiatives && total < 25) {
          featuring.push({
            title: feature.CaseInitiatives,
            link: getKnowledgeDynamicUrl(feature.uri),
          });
          total++;
        }
      });
      const infoboxName = getInfoBoxTitle(infobox);
      const infoboxCategory = getInfoBoxType(infobox);
      const featuringProps = getCarousel(
        featuring,
        relatedIntiative,
        teaseRow,
        infoboxName,
        infoboxCategory,
      );
      teaseRow++;
      pageBody.children = (
        <>
          {pageBody.children}
          <TeaseCarousel {...featuringProps} />
        </>
      );
    }

    if (infobox.caseInfobox.caseTopicsWithConceptUri?.length) {
      title = "Topic";
      total = 0;
      featuring = [];

      infobox.caseInfobox.caseTopicsWithConceptUri.forEach((feature) => {
        if (feature.topics && total < 25) {
          featuring.push({
            title: feature.topics,
            link: getKnowledgeDynamicUrl(feature.uri),
          });
          total++;
        }
      });
      const infoboxName = getInfoBoxTitle(infobox);
      const infoboxCategory = getInfoBoxType(infobox);
      const featuringProps = getCarousel(
        featuring,
        title,
        teaseRow,
        infoboxName,
        infoboxCategory,
      );
      teaseRow++;
      pageBody.children = (
        <>
          {pageBody.children}
          <TeaseCarousel {...featuringProps} />
        </>
      );
    }
    if (infobox.caseInfobox.caseGeoAreaWithConceptUri?.length) {
      title = "Geographic Area";
      total = 0;
      featuring = [];

      infobox.caseInfobox.caseGeoAreaWithConceptUri.forEach((feature) => {
        if (feature.geoArea && total < 25) {
          featuring.push({
            title: feature.geoArea,
            link: getKnowledgeDynamicUrl(feature.uri),
          });
          total++;
        }
      });
      const infoboxName = getInfoBoxTitle(infobox);
      const infoboxCategory = getInfoBoxType(infobox);
      const featuringProps = getCarousel(
        featuring,
        title,
        teaseRow,
        infoboxName,
        infoboxCategory,
      );
      teaseRow++;
      pageBody.children = (
        <>
          {pageBody.children}
          <TeaseCarousel {...featuringProps} />
        </>
      );
    }
    if (infobox.caseInfobox.caseIndustryWithConceptUri?.length) {
      title = "Industry";
      total = 0;
      featuring = [];

      infobox.caseInfobox.caseIndustryWithConceptUri.forEach((feature) => {
        if (feature.aboutIndustry && total < 25) {
          featuring.push({
            title: feature.aboutIndustry,
            link: getKnowledgeDynamicUrl(feature.uri),
          });
          total++;
        }
      });
      const infoboxName = getInfoBoxTitle(infobox);
      const infoboxCategory = getInfoBoxType(infobox);
      const featuringProps = getCarousel(
        featuring,
        title,
        teaseRow,
        infoboxName,
        infoboxCategory,
      );
      teaseRow++;
      pageBody.children = (
        <>
          {pageBody.children}
          <TeaseCarousel {...featuringProps} />
        </>
      );
    }
    if (infobox.caseInfobox.caseFacultiesOverYears?.length) {
      const teaseTitle = "Taught by Faculty";
      const truncatorTitle = {
        isSmall: false,
        title: teaseTitle,
      };

      total = 0;
      authorTease4Up = [];
      authorTeaseTruncated = [];

      handleOnClick = () => {
        // eslint-disable-next-line camelcase
        dataLayerEvent.event_data.infobox_item_type = teaseTitle;
      };

      const infoboxName = getInfoBoxTitle(infobox);
      const infoboxCategory = getInfoBoxType(infobox);
      infobox.caseInfobox.caseFacultiesOverYears.forEach((faculty) => {
        if (faculty.facultyName && total < 25) {
          const facultyImage = getFacultyImage(faculty);
          /* eslint-disable camelcase */
          dataLayerEvent.event_data.infobox_name = infoboxName;
          dataLayerEvent.event_data.infobox_category = infoboxCategory;
          /* eslint-enable camelcase */
          const participantStoryTeaseContent: ParticipantStoryTeaseProps = {
            type: "ParticipantStory",
            name: faculty.facultyName,
            role: `Years: ${faculty.yearsTaught?.replaceAll(",", ", ")}`,
            link: getKnowledgeDynamicUrl(faculty.uri) || "",
            image: {
              src: facultyImage.src,
              alt: facultyImage.alt,
            },
            dataLayer: dataLayerEvent,
            onClick: handleOnClick,
          };

          if (total < 4) {
            authorTease4Up.push({
              name: "ParticipantStoryTease",
              props: participantStoryTeaseContent,
            });
          } else {
            authorTeaseTruncated.push({
              name: "ParticipantStoryTease",
              props: participantStoryTeaseContent,
            });
          }
          total++;
        }
      });

      pageBody.children = (
        <>
          {pageBody.children}

          {authorTease4Up.length > 0 && (
            <TeaseRow
              teases={authorTease4Up}
              align="full"
              headerContent={truncatorTitle}
              teasesPerRow="4"
            />
          )}
          {authorTeaseTruncated.length > 0 && (
            <Truncator
              labelHidden="Read more"
              labelVisible="Read less"
              align="full"
            >
              <TeaseRow
                teases={authorTeaseTruncated}
                teasesPerRow="4"
                align="wide"
                wrap="wrap"
              />
            </Truncator>
          )}
        </>
      );
    }
  }

  if (infobox.courseInfobox) {
    //Faculty, Case, Topic,
    const teaseTitle = "Faculty Instructors";
    const truncatorTitle = {
      isSmall: false,
      title: teaseTitle,
    };
    const infoboxName = getInfoBoxTitle(infobox);
    const infoboxCategory = getInfoBoxType(infobox);
    total = 0;
    featuring = [];

    if (infobox.courseInfobox.courseCasesOverYears?.length) {
      title = "Assigned Cases";
      total = 0;
      featuring = [];

      infobox.courseInfobox.courseCasesOverYears.forEach((feature) => {
        if (feature.caseTitle && total < 25) {
          featuring.push({
            title: feature.caseTitle,
            // dek: `Years Taught: ${feature.yearsTaught}`,
            link: getKnowledgeDynamicUrl(feature.uri),
          });
          total++;
        }
      });
      const infoboxName = getInfoBoxTitle(infobox);
      const infoboxCategory = getInfoBoxType(infobox);
      const featuringProps = getCarousel(
        featuring,
        title,
        teaseRow,
        infoboxName,
        infoboxCategory,
      );
      teaseRow++;
      pageBody.children = (
        <>
          {pageBody.children}
          <TeaseCarousel {...featuringProps} />
        </>
      );
    }

    if (infobox.courseInfobox.courseTopicsWithUri?.length) {
      title = "Topics";
      total = 0;
      featuring = [];

      infobox.courseInfobox.courseTopicsWithUri.forEach((feature) => {
        if (feature.courseTopics && total < 25) {
          featuring.push({
            title: feature.courseTopics,
            link: getKnowledgeDynamicUrl(feature.uri),
          });
          total++;
        }
      });
      const featuringProps = getCarousel(
        featuring,
        title,
        teaseRow,
        infoboxName,
        infoboxCategory,
      );
      teaseRow++;
      pageBody.children = (
        <>
          {pageBody.children}
          <TeaseCarousel {...featuringProps} />
        </>
      );
    }

    if (infobox.courseInfobox.courseFacultiesOverYears?.length) {
      total = 0;
      handleOnClick = () => {
        // eslint-disable-next-line camelcase
        dataLayerEvent.event_data.infobox_item_type = teaseTitle;
      };
      infobox.courseInfobox.courseFacultiesOverYears.forEach((feature) => {
        if (feature.facultyName && total < 25) {
          /* eslint-disable camelcase */
          dataLayerEvent.event_data.infobox_name = infoboxName;
          dataLayerEvent.event_data.infobox_category = infoboxCategory;
          dataLayerEvent.event_data.infobox_item_type = teaseTitle;
          /* eslint-enable camelcase */
          const facultyImage = getFacultyImage(feature);
          const participantStoryTeaseContent: ParticipantStoryTeaseProps = {
            type: "ParticipantStory",
            name: feature.facultyName,
            role: `Years: ${feature.yearsTaught}`,
            link: getKnowledgeDynamicUrl(feature.uri) || "",
            image: {
              src: facultyImage.src,
              alt: facultyImage.alt,
            },
            onClick: handleOnClick,
            dataLayer: dataLayerEvent,
          };

          if (total < 4) {
            authorTease4Up.push({
              name: "ParticipantStoryTease",
              props: participantStoryTeaseContent,
            });
          } else {
            authorTeaseTruncated.push({
              name: "ParticipantStoryTease",
              props: participantStoryTeaseContent,
            });
          }
          total++;
        }
      });

      pageBody.children = (
        <>
          {pageBody.children}

          {authorTease4Up.length > 0 && (
            <TeaseRow
              teases={authorTease4Up}
              align="full"
              headerContent={truncatorTitle}
              teasesPerRow="4"
            />
          )}
          {authorTeaseTruncated.length > 0 && (
            <Truncator
              labelHidden="Read more"
              labelVisible="Read less"
              align="full"
            >
              <TeaseRow
                teases={authorTeaseTruncated}
                teasesPerRow="4"
                align="wide"
                wrap="wrap"
              />
            </Truncator>
          )}
        </>
      );
    }
  }

  if (infobox.facultyInfobox) {
    teaseRow = 0;
    // Course, case, initiative, "Featured in" publication
    if (infobox.facultyInfobox.coursesTaught?.length) {
      title = "Course Taught";
      total = 0;
      featuring = [];

      infobox.facultyInfobox.coursesTaught.forEach((feature) => {
        if (feature.name && total < 25) {
          featuring.push({
            title: feature.name,
            link: getKnowledgeDynamicUrl(feature.uri),
          });
          total++;
        }
      });
      const infoboxName = getInfoBoxTitle(infobox);
      const infoboxCategory = getInfoBoxType(infobox);
      const featuringCourses = getCarousel(
        featuring,
        title,
        teaseRow,
        infoboxName,
        infoboxCategory,
      );
      teaseRow++;
      pageBody.children = (
        <>
          {pageBody.children}
          <TeaseCarousel {...featuringCourses} />
        </>
      );
    }

    if (infobox.facultyInfobox.facultyPublications?.length) {
      const cases = "Cases";
      total = 0;
      featuring = [];
      const casePublications =
        infobox.facultyInfobox.facultyPublications.filter(
          (u) => u.contentType === "Case",
        );
      const noneCasePublications =
        infobox.facultyInfobox.facultyPublications.filter(
          (u) => u.contentType !== "Case",
        );

      casePublications.forEach((feature) => {
        if (feature.title && total < 25) {
          featuring.push({
            title: feature.title,
            link: getKnowledgeDynamicUrl(feature.uri),
          });
          total++;
        }
      });
      if (featuring.length > 0) {
        const infoboxName = getInfoBoxTitle(infobox);
        const infoboxCategory = getInfoBoxType(infobox);
        const featuringProps = getCarousel(
          featuring,
          cases,
          teaseRow,
          infoboxName,
          infoboxCategory,
        );
        teaseRow++;
        pageBody.children = (
          <>
            {pageBody.children}
            <TeaseCarousel {...featuringProps} />
          </>
        );
      }

      const publications = "Publications";
      total = 0;
      featuring = [];

      noneCasePublications.forEach((feature) => {
        if (feature.title && total < 25) {
          featuring.push({
            title: feature.title,
            link: feature.url,
          });
          total++;
        }
      });
      if (featuring.length > 0) {
        const infoboxName = getInfoBoxTitle(infobox);
        const infoboxCategory = getInfoBoxType(infobox);
        const featuringProps = getCarousel(
          featuring,
          publications,
          teaseRow,
          infoboxName,
          infoboxCategory,
        );
        teaseRow++;
        pageBody.children = (
          <>
            {pageBody.children}
            <TeaseCarousel {...featuringProps} />
          </>
        );
      }
    }

    if (infobox.facultyInfobox.initiativeAffiliationWithConceptUri?.length) {
      title = "Initiative Affiliation";
      total = 0;
      featuring = [];

      infobox.facultyInfobox.initiativeAffiliationWithConceptUri.forEach(
        (feature) => {
          if (feature.initiativeAffiliation && total < 25) {
            featuring.push({
              title: feature.initiativeAffiliation,
              link: getKnowledgeDynamicUrl(feature.uri),
            });
            total++;
          }
        },
      );
      const infoboxName = getInfoBoxTitle(infobox);
      const infoboxCategory = getInfoBoxType(infobox);
      const featuringProps = getCarousel(
        featuring,
        title,
        teaseRow,
        infoboxName,
        infoboxCategory,
      );
      teaseRow++;
      pageBody.children = (
        <>
          {pageBody.children}
          <TeaseCarousel {...featuringProps} />
        </>
      );
    }

    if (infobox.facultyInfobox.featuringArticles?.length) {
      title = `${infobox.facultyInfobox.displayName} Featured In`;
      total = 0;
      featuring = [];

      infobox.facultyInfobox.featuringArticles.forEach((feature) => {
        if (feature.title && total < 25) {
          const url =
            feature.contentType === "Case"
              ? getKnowledgeDynamicUrl(feature.uri)
              : feature.url;
          featuring.push({
            title: feature.title,
            link: url,
          });
          total++;
        }
      });
      const infoboxName = getInfoBoxTitle(infobox);
      const infoboxCategory = getInfoBoxType(infobox);
      const featuringProps = getCarousel(
        featuring,
        title,
        teaseRow,
        infoboxName,
        infoboxCategory,
      );
      teaseRow++;
      pageBody.children = (
        <>
          {pageBody.children}
          <TeaseCarousel {...featuringProps} />
        </>
      );
    }
  }

  if (infobox.initiativeInfobox) {
    //Case,Publication,Alumni,Faculty
    props.type = getInfoBoxType(infobox);
    const infoboxName = getInfoBoxTitle(infobox);
    const infoboxCategory = getInfoBoxType(infobox);
    teaseRow = 0;
    if (infobox.initiativeInfobox.conceptUri) {
      props.url = infobox.initiativeInfobox.conceptUri;
    }
    if (infobox.initiativeInfobox.initiativeTitle) {
      props.name = infobox.initiativeInfobox.initiativeTitle;
    }

    if (infobox.initiativeInfobox.conceptUri && props.link) {
      props.link.href = infobox.initiativeInfobox?.conceptUri;
      props.link.children = props.link.href ? "Full profile" : undefined;
    }
    if (infobox.initiativeInfobox.intiativeDescription) {
      props.description = infobox.initiativeInfobox.intiativeDescription;
    }

    if (infobox.initiativeInfobox.relatedPublications?.length) {
      const casePublications =
        infobox.initiativeInfobox.relatedPublications.filter(
          (u) => u.contentType === "Case",
        );
      const noneCasePublications =
        infobox.initiativeInfobox.relatedPublications.filter(
          (u) => u.contentType !== "Case",
        );
      title = "Related Cases";
      total = 0;
      featuring = [];

      casePublications.forEach((feature) => {
        if (feature.name && total < 25) {
          featuring.push({
            title: feature.name,
            link: getKnowledgeDynamicUrl(feature.uri),
          });
          total++;
        }
      });
      const featuringProps = getCarousel(
        featuring,
        title,
        teaseRow,
        infoboxName,
        infoboxCategory,
      );
      teaseRow++;
      pageBody.children = (
        <>
          {pageBody.children}
          <TeaseCarousel {...featuringProps} />
        </>
      );

      const publicationsTitle = "Related Publications";
      total = 0;
      featuring = [];

      noneCasePublications.forEach((feature) => {
        if (feature.name && total < 25) {
          featuring.push({
            title: feature.name,
            link: feature.url,
          });
          total++;
        }
      });
      if (featuring.length > 0) {
        const infoboxName = getInfoBoxTitle(infobox);
        const infoboxCategory = getInfoBoxType(infobox);
        const featuringProps = getCarousel(
          featuring,
          publicationsTitle,
          teaseRow,
          infoboxName,
          infoboxCategory,
        );
        pageBody.children = (
          <>
            {pageBody.children}
            <TeaseCarousel {...featuringProps} />
          </>
        );
      }
    }
    if (infobox.initiativeInfobox.relatedAlum?.length) {
      title = "Affiliated Alumni";
      total = 0;
      featuring = [];

      infobox.initiativeInfobox.relatedAlum.forEach((feature) => {
        if (feature.name && total < 25) {
          featuring.push({
            title: feature.name,
            link: getKnowledgeDynamicUrl(feature.uri),
          });
          total++;
        }
      });
      const infoboxName = getInfoBoxTitle(infobox);
      const infoboxCategory = getInfoBoxType(infobox);
      const featuringProps = getCarousel(
        featuring,
        title,
        teaseRow,
        infoboxName,
        infoboxCategory,
      );
      teaseRow++;
      pageBody.children = (
        <>
          {pageBody.children}
          <TeaseCarousel {...featuringProps} />
        </>
      );
    }

    if (infobox.initiativeInfobox.relatedFaculty?.length) {
      total = 0;
      featuring = [];
      const teaseTitle = "Affiliated Faculty";
      const title = {
        isSmall: false,
        title: teaseTitle,
      };

      infobox.initiativeInfobox.relatedFaculty.forEach((feature) => {
        if (feature.name && total < 25) {
          /* eslint-disable camelcase */
          dataLayerEvent.event_data.infobox_name = infoboxName;
          dataLayerEvent.event_data.infobox_category = infoboxCategory;
          dataLayerEvent.event_data.infobox_item_type = teaseTitle;
          /* eslint-enable camelcase */
          const facultyImage = getFacultyImage(feature);
          const participantStoryTeaseContent: ParticipantStoryTeaseProps = {
            type: "ParticipantStory",
            name: feature.name,
            link: getKnowledgeDynamicUrl(feature.uri) || "",
            image: {
              src: facultyImage.src,
              alt: facultyImage.alt,
            },
            dataLayer: dataLayerEvent,
          };

          if (total < 4) {
            authorTease4Up.push({
              name: "ParticipantStoryTease",
              props: participantStoryTeaseContent,
            });
          } else {
            authorTeaseTruncated.push({
              name: "ParticipantStoryTease",
              props: participantStoryTeaseContent,
            });
          }
          total++;
        }
      });
      teaseRow++;
      pageBody.children = (
        <>
          {pageBody.children}

          {authorTease4Up.length > 0 && (
            <TeaseRow
              teases={authorTease4Up}
              align="full"
              headerContent={title}
              teasesPerRow="4"
            />
          )}
          {authorTeaseTruncated.length > 0 && (
            <Truncator
              labelHidden="Read more"
              labelVisible="Read less"
              align="full"
            >
              <TeaseRow
                teases={authorTeaseTruncated}
                teasesPerRow="4"
                align="wide"
                wrap="wrap"
              />
            </Truncator>
          )}
        </>
      );
    }
    if (infobox.initiativeInfobox.relatedAlum?.length) {
      total = 0;
      authorTease4Up = [];
      authorTeaseTruncated = [];
      const teaseTitle = "Affiliated Alumni";
      const alumniTitle = {
        isSmall: false,
        title: teaseTitle,
      };

      infobox.initiativeInfobox.relatedAlum.forEach((feature) => {
        if (feature.name && total < 25) {
          /* eslint-disable camelcase */
          dataLayerEvent.event_data.infobox_name = infoboxName;
          dataLayerEvent.event_data.infobox_category = infoboxCategory;
          dataLayerEvent.event_data.infobox_item_type = teaseTitle;
          /* eslint-enable camelcase */
          const alumniImage = getAlumniImage(feature);
          const participantStoryTeaseContent: ParticipantStoryTeaseProps = {
            type: "ParticipantStory",
            name: feature.name,
            link: getKnowledgeDynamicUrl(feature.uri) || "",
            image: {
              src: alumniImage.src,
              alt: alumniImage.alt,
            },
            dataLayer: dataLayerEvent,
          };

          if (total < 4) {
            authorTease4Up.push({
              name: "ParticipantStoryTease",
              props: participantStoryTeaseContent,
            });
          } else {
            authorTeaseTruncated.push({
              name: "ParticipantStoryTease",
              props: participantStoryTeaseContent,
            });
          }
          total++;
        }
      });
      pageBody.children = (
        <>
          {pageBody.children}
          {authorTease4Up.length > 0 && (
            <TeaseRow
              teases={authorTease4Up}
              align="full"
              headerContent={alumniTitle}
              teasesPerRow="4"
            />
          )}
          {authorTeaseTruncated.length > 0 && (
            <Truncator
              labelHidden="Read more"
              labelVisible="Read less"
              align="full"
            >
              <TeaseRow
                teases={authorTeaseTruncated}
                teasesPerRow="4"
                align="wide"
                wrap="wrap"
              />
            </Truncator>
          )}
        </>
      );
    }
  }

  if (infobox.companyInfobox) {
    // Organization order: Case, Faculty, Alumni
    teaseRow = 0;
    const infoboxName = getInfoBoxTitle(infobox);
    const infoboxCategory = getInfoBoxType(infobox);
    if (infobox.companyInfobox.facultyAuthors) {
      const facultyAuthors: Array<KnowledgeFacultyAuthors> =
        infobox.companyInfobox.facultyAuthors;
      const teaseTitle = "Faculty";
      if (facultyAuthors.length) {
        title = "Faculty Publications";
        const nonFacultyTitle = "Publications (No Active Faculty)";
        const truncatorTitle = {
          isSmall: false,
          title: teaseTitle,
        };

        total = 0;
        let facultyTotal = 0;
        let nonFacultytotal = 0;
        featuring = [];
        featuring1 = [];
        handleOnClick = () => {
          // eslint-disable-next-line camelcase
          dataLayerEvent.event_data.infobox_item_type = teaseTitle;
        };

        infobox.companyInfobox.facultyAuthors.forEach((feature) => {
          feature.publicationsAboutCompany?.forEach((publication) => {
            const url =
              publication.contentType === "Case"
                ? getKnowledgeDynamicUrl(publication.uri)
                : publication.url;
            if (feature.facultyName && total < 25) {
              featuring.push({
                title: publication.name || "",
                dek: [feature.facultyName, feature.position]
                  .filter(Boolean)
                  .join(", "),
                meta: [
                  publication.displayDate,
                  "[" + publication.contentType + "]",
                ].join(", "),
                link: url,
              });
              total++;
            } else if (!feature.facultyName) {
              if (nonFacultytotal < 25) {
                featuring1.push({
                  title: publication.name || "",
                  meta: [
                    publication.displayDate,
                    "[" + publication.contentType + "]",
                  ].join(", "),
                  link: url,
                });
                nonFacultytotal++;
              }
            }
          });

          if (feature.facultyName && facultyTotal < 25) {
            const facultyImage = getFacultyImage(feature);
            /* eslint-disable camelcase */
            dataLayerEvent.event_data.infobox_name = infoboxName;
            dataLayerEvent.event_data.infobox_category = infoboxCategory;
            dataLayerEvent.event_data.infobox_item_type = teaseTitle;
            /* eslint-enable camelcase */
            const participantStoryTeaseContent: ParticipantStoryTeaseProps = {
              type: "ParticipantStory",
              name: feature.facultyName,
              role: feature.position,
              link: getKnowledgeDynamicUrl(feature.uri) || "",
              image: {
                src: facultyImage.src,
                alt: facultyImage.alt,
              },
              dataLayer: dataLayerEvent,
              onClick: handleOnClick,
            };

            if (facultyTotal < 4) {
              authorTease4Up.push({
                name: "ParticipantStoryTease",
                props: participantStoryTeaseContent,
              });
            } else {
              authorTeaseTruncated.push({
                name: "ParticipantStoryTease",
                props: participantStoryTeaseContent,
              });
            }
            facultyTotal++;
          }
        });
        const featuringProps = getCarousel(
          featuring,
          title,
          teaseRow,
          infoboxName,
          infoboxCategory,
        );
        teaseRow++;
        const nonFacultyFeaturingProps = getCarousel(
          featuring1,
          nonFacultyTitle,
          teaseRow,
          infoboxName,
          infoboxCategory,
        );
        teaseRow++;

        pageBody.children = (
          <>
            {pageBody.children}
            <TeaseCarousel {...featuringProps} />
            {featuring1.length > 0 && (
              <TeaseCarousel {...nonFacultyFeaturingProps} />
            )}
            {authorTease4Up.length > 0 && (
              <TeaseRow
                teases={authorTease4Up}
                align="full"
                headerContent={truncatorTitle}
                teasesPerRow="4"
              />
            )}
            {authorTeaseTruncated.length > 0 && (
              <Truncator
                labelHidden="Read more"
                labelVisible="Read less"
                align="full"
              >
                <TeaseRow
                  teases={authorTeaseTruncated}
                  teasesPerRow="4"
                  align="wide"
                  wrap="wrap"
                />
              </Truncator>
            )}
          </>
        );
      }
    }
    if (infobox.companyInfobox.alumni?.length) {
      total = 0;
      featuring = [];
      authorTease4Up = [];
      authorTeaseTruncated = [];
      const teaseTitle = "Alumni";
      const truncatorTitle = {
        isSmall: false,
        title: teaseTitle,
      };
      handleOnClick = () => {
        // eslint-disable-next-line camelcase
        dataLayerEvent.event_data.infobox_item_type = teaseTitle;
      };

      infobox.companyInfobox.alumni.forEach((feature) => {
        if (feature.name && total < 25) {
          /* eslint-disable camelcase */
          dataLayerEvent.event_data.infobox_name = infoboxName;
          dataLayerEvent.event_data.infobox_category = infoboxCategory;
          dataLayerEvent.event_data.infobox_item_type = teaseTitle;
          /* eslint-enable camelcase */
          const alumniImage = getAlumniImage(feature);
          const participantStoryTeaseContent: ParticipantStoryTeaseProps = {
            type: "ParticipantStory",
            name: feature.name,
            role: feature.degree
              ? feature.degree.map((company) => company).join(", ")
              : "",
            link: getKnowledgeDynamicUrl(feature.uri) || "",
            image: {
              src: alumniImage.src,
              alt: alumniImage.alt,
            },
            onClick: handleOnClick,
            dataLayer: dataLayerEvent,
          };
          if (total < 4) {
            authorTease4Up.push({
              name: "ParticipantStoryTease",
              props: participantStoryTeaseContent,
            });
          } else {
            authorTeaseTruncated.push({
              name: "ParticipantStoryTease",
              props: participantStoryTeaseContent,
            });
          }

          total++;
        }
      });
      pageBody.children = (
        <>
          {pageBody.children}
          {authorTease4Up.length > 0 && (
            <TeaseRow
              teases={authorTease4Up}
              align="full"
              headerContent={truncatorTitle}
              teasesPerRow="4"
            />
          )}
          {authorTeaseTruncated.length > 0 && (
            <Truncator
              labelHidden="Read more"
              labelVisible="Read less"
              align="full"
            >
              <TeaseRow
                teases={authorTeaseTruncated}
                teasesPerRow="4"
                align="wide"
                wrap="wrap"
              />
            </Truncator>
          )}
        </>
      );
    }
  }

  if (infobox.topicInfobox) {
    //Broader Concept, Narrower Concept, Course, Case, Publications, WK, Alumni
    teaseRow = 0;
    if (infobox.topicInfobox.broaderConcepts?.length) {
      title = "Broader Concepts";
      total = 0;
      featuring = [];

      infobox.topicInfobox.broaderConcepts.forEach((feature) => {
        if (feature.title && total < 25) {
          featuring.push({
            title: feature.title,
            link: getKnowledgeDynamicUrl(feature.uri),
          });
          total++;
        }
      });
      const infoboxName = getInfoBoxTitle(infobox);
      const infoboxCategory = getInfoBoxType(infobox);
      const featuringProps = getCarousel(
        featuring,
        title,
        teaseRow,
        infoboxName,
        infoboxCategory,
      );
      teaseRow++;
      pageBody.children = (
        <>
          {pageBody.children}
          <TeaseCarousel {...featuringProps} />
        </>
      );
    }
    if (infobox.topicInfobox.narrowerConcepts?.length) {
      title = "Narrower Concepts";
      total = 0;
      featuring = [];

      infobox.topicInfobox.narrowerConcepts.forEach((feature) => {
        if (feature.title && total < 25) {
          featuring.push({
            title: feature.title,
            link: getKnowledgeDynamicUrl(feature.uri),
          });
          total++;
        }
      });
      const infoboxName = getInfoBoxTitle(infobox);
      const infoboxCategory = getInfoBoxType(infobox);
      const featuringProps1 = getCarousel(
        featuring,
        title,
        teaseRow,
        infoboxName,
        infoboxCategory,
      );
      teaseRow++;
      pageBody.children = (
        <>
          {pageBody.children}
          <TeaseCarousel {...featuringProps1} />
        </>
      );
    }
    if (infobox.topicInfobox.courses?.length) {
      title = "Related Courses";
      total = 0;
      featuring = [];

      infobox.topicInfobox.courses.forEach((feature) => {
        if (feature.name && total < 25) {
          featuring.push({
            title: feature.name,
            dek: feature.description,
            meta: `Course Number: ${feature.number}, Term: ${feature.term}`,
            link: getKnowledgeDynamicUrl(feature.uri),
          });
          total++;
        }
      });
      const infoboxName = getInfoBoxTitle(infobox);
      const infoboxCategory = getInfoBoxType(infobox);
      const featuringProps = getCarousel(
        featuring,
        title,
        teaseRow,
        infoboxName,
        infoboxCategory,
      );
      teaseRow++;
      pageBody.children = (
        <>
          {pageBody.children}
          <TeaseCarousel {...featuringProps} />
        </>
      );
    }
    if (infobox.topicInfobox.facultyPublicationsDetails?.length) {
      total = 0;
      featuring = [];

      const publicationsTitle = "Faculty Publications";
      total = 0;
      featuring = [];

      infobox.topicInfobox.facultyPublicationsDetails.forEach((feature) => {
        if (feature.publicationName && total < 25) {
          const url =
            feature.contentType === "Case"
              ? getKnowledgeDynamicUrl(feature.uri)
              : feature.url;
          featuring.push({
            title: feature.publicationName,
            meta: [feature.displayDate, "[" + feature.contentType + "]"].join(
              ", ",
            ),
            link: url,
          });
          total++;
        }
      });
      if (featuring.length > 0) {
        const infoboxName = getInfoBoxTitle(infobox);
        const infoboxCategory = getInfoBoxType(infobox);
        const featuringProps = getCarousel(
          featuring,
          publicationsTitle,
          teaseRow,
          infoboxName,
          infoboxCategory,
        );
        teaseRow++;
        pageBody.children = (
          <>
            {pageBody.children}
            <TeaseCarousel {...featuringProps} />
          </>
        );
      }
    }
  }
  return pageBody;
}
